import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { hotelService, fileService } from "@/services/ServiceRegistry";
import {
  Hotel as HotelBrand,
  CreateHotelDto,
  UpdateHotelDto,
  HotelFilters,
} from "@/types/hotel";
import { HotelFormData } from "../utils/hotelSchema";
import {
  FileUploadOutcome,
  resolveUploadedFileId,
} from "../utils/fileUploadOutcome";

const attemptUpload = async (
  file: File,
  tenantId?: string,
): Promise<FileUploadOutcome> => {
  try {
    return {
      ok: true,
      fileId: await fileService.uploadFileWithPresignedUrl(file, tenantId),
    };
  } catch (error) {
    return { ok: false, error };
  }
};

export const hotelKeys = {
  all: ["hotels"] as const,
  lists: () => [...hotelKeys.all, "list"] as const,
  list: (filters?: HotelFilters) => [...hotelKeys.lists(), filters] as const,
  details: () => [...hotelKeys.all, "detail"] as const,
  detail: (id: string) => [...hotelKeys.details(), id] as const,
};

export const useHotels = (filters?: HotelFilters, enabled = true) => {
  return useQuery({
    queryKey: hotelKeys.list(filters),
    queryFn: () => hotelService.getHotels(filters),
    staleTime: 5 * 60 * 1000,
    enabled,
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Hotel mutations take `tenantId` in tenant mode (`?tenantId=`, platform
 * admins): requests carry it in `x-tenant-id` (DV-168).
 */
export const useCreateHotel = (tenantId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (hotelData: CreateHotelDto) =>
      hotelService.createHotel(hotelData, tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hotelKeys.lists() });
    },
  });
};

export const useUpdateHotel = (tenantId?: string) => {
  const queryClient = useQueryClient();

  return useMutation<HotelBrand, Error, { id: string; data: UpdateHotelDto }>({
    mutationFn: ({ id, data }: { id: string; data: UpdateHotelDto }) =>
      hotelService.updateHotel(id, data, tenantId),
    onSuccess: (response: HotelBrand) => {
      queryClient.setQueryData(hotelKeys.detail(response.id), response);
      queryClient.setQueriesData<HotelBrand | null>(
        { queryKey: ["userHotel"] },
        (current) =>
          current?.id === response.id ? { ...current, ...response } : current,
      );
      queryClient.invalidateQueries({ queryKey: hotelKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["userHotel"] });
    },
  });
};

export const useDeleteHotel = (tenantId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (hotelId: string) =>
      hotelService.deleteHotel(hotelId, tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hotelKeys.lists() });
    },
  });
};

export const useActivateHotel = (tenantId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (hotelId: string) =>
      hotelService.activateHotel(hotelId, tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hotelKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["userHotel"] });
    },
  });
};

export const useDeactivateHotel = (tenantId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (hotelId: string) =>
      hotelService.deactivateHotel(hotelId, tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hotelKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["userHotel"] });
    },
  });
};

export const useHotelCreate = (tenantId?: string) => {
  const createHotelMutation = useCreateHotel(tenantId);

  const handleHotelCreate = async (
    hotelData: HotelFormData,
    logoFile?: File,
    mapFile?: File,
  ) => {
    try {
      let logoFileId: string | undefined;

      if (logoFile) {
        try {
          logoFileId = await fileService.uploadFileWithPresignedUrl(
            logoFile,
            tenantId,
          );
        } catch (logoError) {
          console.error("Failed to upload logo:", logoError);
          throw logoError;
        }
      }

      let mapFileId: string | undefined;

      if (mapFile) {
        try {
          mapFileId = await fileService.uploadFileWithPresignedUrl(
            mapFile,
            tenantId,
          );
        } catch (error) {
          console.error("Failed to upload map:", error);
        }
      }

      // logoId формы — локальный blob-URL предпросмотра, на сервер не уходит
      // eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars
      const { logoId: _previewLogo, ...hotelFields } = hotelData;
      const createHotelData: CreateHotelDto = {
        ...hotelFields,
        ...(logoFileId ? { logoId: logoFileId } : {}),
        mapFileId,
      };

      await createHotelMutation.mutateAsync(createHotelData);
    } catch (error) {
      console.error("Failed to create hotel:", error);
      throw error;
    }
  };

  return {
    handleHotelSubmit: handleHotelCreate,
    isLoading: createHotelMutation.isPending,
    error: createHotelMutation.error,
  };
};

export const useHotelUpdate = (hotelId: string, tenantId?: string) => {
  const updateHotelMutation = useUpdateHotel(tenantId);

  const handleHotelUpdate = async (
    hotelData: HotelFormData,
    logoFile?: File,
    currentLogoId?: string,
    mapFile?: File,
    currentMapId?: string,
  ) => {
    try {
      let logoFileId: string | undefined = currentLogoId;

      if (logoFile) {
        logoFileId = resolveUploadedFileId(
          "logo",
          await attemptUpload(logoFile, tenantId),
        );
      }

      let mapFileId: string | undefined = currentMapId;

      if (mapFile) {
        mapFileId = resolveUploadedFileId(
          "map",
          await attemptUpload(mapFile, tenantId),
        );
      }

      const { managerIds, ...hotelFields } = hotelData;
      const updateHotelData: UpdateHotelDto = {
        ...hotelFields,
        ...(managerIds.length ? { managerIds } : {}),
        logoId: logoFileId,
        mapFileId,
      };

      await updateHotelMutation.mutateAsync({
        id: hotelId,
        data: updateHotelData,
      });
    } catch (error) {
      console.error("Failed to update hotel:", error);
      throw error;
    }
  };

  return {
    handleHotelSubmit: handleHotelUpdate,
    isLoading: updateHotelMutation.isPending,
    error: updateHotelMutation.error,
  };
};
